/**
 * Public CRM solution teaser for InterviewFlow (no auth required).
 */
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InterviewFlowNavbar from '../../components/InterviewFlow/InterviewFlowNavbar';
import ErrorBoundary from '../../components/ErrorBoundary';

const FONT_LINK_ID = 'interviewflow-umbrella-fonts';
const DISPLAY = '"Fraunces", Georgia, serif';
const BODY = '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif';
const INK = '#0c1a2e';
const ACCENT = '#1976d2';

function ensureFonts() {
  if (typeof document === 'undefined' || document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Source+Sans+3:wght@400;600;700&display=swap';
  document.head.appendChild(link);
}

const STEPS = [
  { title: 'Qualify', body: 'A short interview captures intent, budget signals, and next steps.' },
  { title: 'Stage', body: 'Answers land in lead stages, tasks, and notes — not a buried chat log.' },
  { title: 'Follow up', body: 'Campaigns and AI fields keep the conversation moving in CRM.' },
];

export default function CrmSolutionPage() {
  const navigate = useNavigate();

  useEffect(() => {
    ensureFonts();
  }, []);

  return (
    <ErrorBoundary>
      <Box
        sx={{
          minHeight: '100vh',
          fontFamily: BODY,
          color: INK,
          background:
            'radial-gradient(900px 420px at 12% 0%, rgba(25, 118, 210, 0.16), transparent 55%),'
            + 'linear-gradient(180deg, #eef3f9 0%, #f7f9fc 100%)',
        }}
      >
        <InterviewFlowNavbar />
        <Container maxWidth="md" sx={{ pt: { xs: 14, md: 16 }, pb: 8 }}>
          <Typography
            component="p"
            sx={{
              fontFamily: DISPLAY, fontWeight: 600, color: ACCENT, mb: 1, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.85rem',
            }}
          >
            Solution
          </Typography>
          <Typography
            component="h1"
            data-testid="crm-solution-title"
            sx={{
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.75rem' },
              letterSpacing: '-0.03em',
              mb: 2,
            }}
          >
            CRM interviews
          </Typography>
          <Typography sx={{
            fontSize: '1.15rem', color: 'rgba(12, 26, 46, 0.72)', mb: 4, maxWidth: 560, lineHeight: 1.55,
          }}
          >
            Qualify leads with a guided conversation, then continue in the CRM workspace — contacts, opportunities, tasks, and campaigns in one place.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2.5,
              mb: 5,
            }}
          >
            {STEPS.map((s) => (
              <Box
                key={s.title}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid rgba(12, 26, 46, 0.1)',
                  bgcolor: 'rgba(255,255,255,0.9)',
                }}
              >
                <Typography sx={{ fontFamily: DISPLAY, fontWeight: 600, mb: 0.75 }}>{s.title}</Typography>
                <Typography sx={{ fontSize: '0.95rem', color: 'rgba(12, 26, 46, 0.68)', lineHeight: 1.45 }}>{s.body}</Typography>
              </Box>
            ))}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              data-testid="crm-solution-signin"
              onClick={() => navigate('/signin', { state: { from: '/crm' } })}
              sx={{
                fontFamily: BODY,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: ACCENT,
                boxShadow: 'none',
                px: 3,
                '&:hover': { bgcolor: '#1565c0', boxShadow: 'none' },
              }}
            >
              Sign in to open CRM
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/studio')}
              sx={{
                fontFamily: BODY,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'rgba(12, 26, 46, 0.28)',
                color: INK,
              }}
            >
              Customize in Studio
            </Button>
          </Stack>
        </Container>
      </Box>
    </ErrorBoundary>
  );
}
